import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";

import type { MapFacts as Facts } from "../../map/mapFacts";

const CAMPS = msg({
  id: "analysis.map.fact.camps",
  message: "Camps: you {mine}, teammates {mates}",
});
const CAMPS_MISSED = msg({
  id: "analysis.map.fact.camps_missed",
  message: "{count, plural, one {# free camp on your half} other {# free camps on your half}}",
});
const RUNES = msg({
  id: "analysis.map.fact.bridge_runes",
  message: "Bridge runes: you {mine} of {total}, teammates average {average}",
});
const PICKUPS = msg({
  id: "analysis.map.fact.crates",
  message: "Crate bonuses: you {count}, teammates average {average}",
});
const BOSS_OURS = msg({
  id: "analysis.map.fact.mid_boss_ours",
  message: "Mid-boss: {ours} of {total} for your team",
});
const BOSS_ENEMY = msg({
  id: "analysis.map.fact.mid_boss_enemy",
  message: "Mid-boss: {total, plural, one {to enemies} other {all # to enemies}}",
});

type Tone = "good" | "even" | "bad";

const DOTS: Record<Tone, string> = {
  good: "bg-map-me",
  even: "bg-subtle",
  bad: "bg-map-missed",
};

// Меньше этой доли от среднего у союзников — отставание (красная плашка), до неё — почти
// как у союзников: «24 бонуса · у союзников 26» не ошибка.
const BEHIND = 0.8;

const versusMates = (mine: number, average: number): Tone =>
  mine >= average ? "good" : mine >= average * BEHIND ? "even" : "bad";

// Итоги карты плашками в одном виде «тема: ты N, союзники M»: лагеря, руны на мостах,
// бонусы, мид-босс. Красная — только заметное отставание. На узком экране плашки
// листаются строкой.
export const MapFacts = ({ facts }: { facts: Facts }) => {
  const { _ } = useLingui();
  const chips: { key: string; tone: Tone; text: MessageDescriptor; values: object }[] = [
    {
      key: "camps",
      tone: "good",
      text: CAMPS,
      values: { mine: facts.camps.mine, mates: facts.camps.mates },
    },
  ];
  if (facts.camps.missed > 0) {
    chips.push({
      key: "missed",
      tone: "bad",
      text: CAMPS_MISSED,
      values: { count: facts.camps.missed },
    });
  }
  if (facts.runes.total > 0) {
    chips.push({
      key: "runes",
      tone: versusMates(facts.runes.mine, facts.runes.matesAverage),
      text: RUNES,
      values: {
        mine: facts.runes.mine,
        total: facts.runes.total,
        average: facts.runes.matesAverage,
      },
    });
  }
  chips.push({
    key: "pickups",
    tone: versusMates(facts.pickups.mine, facts.pickups.matesAverage),
    text: PICKUPS,
    values: { count: facts.pickups.mine, average: facts.pickups.matesAverage },
  });
  if (facts.midBoss.total > 0) {
    chips.push(
      facts.midBoss.ours > 0
        ? { key: "boss", tone: "good", text: BOSS_OURS, values: facts.midBoss }
        : { key: "boss", tone: "bad", text: BOSS_ENEMY, values: facts.midBoss },
    );
  }
  return (
    <ul className="flex [scrollbar-width:none] gap-1.5 overflow-x-auto @lg:flex-wrap @lg:overflow-visible">
      {chips.map((chip) => (
        <li
          key={chip.key}
          data-fact={chip.key}
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-full border bg-background/70 px-2.5 py-1 text-xs font-semibold whitespace-nowrap",
            chip.tone === "bad" ? "border-bad/40 text-bad" : "border-border text-foreground",
          )}
        >
          <span aria-hidden="true" className={cn("size-2.5 rounded-full", DOTS[chip.tone])} />
          {_({ ...chip.text, values: chip.values as Record<string, unknown> })}
        </li>
      ))}
    </ul>
  );
};
